import * as yup from 'yup'

import {
  EVENT_FORM_CONTEXT_CAN_WITNESS_READ_OR_WAIVE,
  EVENT_FORM_CONTEXT_ERRATA_APPLIES,
  EVENT_FORM_CONTEXT_IS_EUO,
  EVENT_FORM_CONTEXT_IS_FINAL_SCRIPT_EVENT,
} from './formContext'

import {
  DR_SPELLINGS_CHAR_LIMIT,
  DR_TIMESTAMPS_CHAR_LIMIT,
} from '@/components/Event/NewScript/constants'
import { transcriptOrderFields } from '@/components/Event/NewScript/schemas/transcriptOrderFields'
import { getMaxCharactersMessage } from '@/components/Event/NewScript/schemas/validation'
import { ReadWaiveAnswer, ScriptAttendee } from '@/types'

export const closingStepSchema = yup.object().shape({
  ...transcriptOrderFields,
  read_waive_answer: yup
    .mixed<ReadWaiveAnswer>()
    .oneOf(['no_answer', 'read', 'waive'])
    .required()
    .default('no_answer')
    // Where the question is asked it must be answered. "No answer" is reserved
    // for the events that are never asked, and is set for them rather than
    // chosen, so it is not one of the answers this step can be completed with.
    // A test rather than a narrower `oneOf`: yup merges whitelists instead of
    // replacing them, so the base union would let "no answer" back in.
    .when(
      [
        `$${EVENT_FORM_CONTEXT_CAN_WITNESS_READ_OR_WAIVE}`,
        `$${EVENT_FORM_CONTEXT_IS_EUO}`,
      ],
      ([canWitnessReadOrWaive, isEUO], schema) =>
        canWitnessReadOrWaive && !isEUO
          ? schema.test(
              'read-waive-answered',
              'One of the options must be selected',
              value => value !== 'no_answer',
            )
          : schema,
    ),
  errata_recipient_attendee_id: yup
    .string()
    .nullable()
    .default(null)
    .when(
      `$${EVENT_FORM_CONTEXT_ERRATA_APPLIES}`,
      ([errataApplies], schema) => {
        if (!errataApplies) {
          return schema
        }

        // Optional: a recipient we do not know is a real outcome. What we must
        // not store is a recipient with no address, since nothing could be
        // sent to them.
        return schema.test(
          'errata-recipient-email',
          'Email is required for the errata recipient',
          function (value) {
            const attendees: ScriptAttendee[] = this.parent.attendees
            const recipient = attendees?.find(
              attendee => attendee.attendee_id === value,
            )

            // `this.parent` holds the raw sibling values, so the attendee
            // schema's trim has not run on them. An address of spaces is no
            // address: the backend reads it as none, leaving the sheet with
            // nowhere to go while this step read as done.
            return !recipient || Boolean(recipient.email?.trim())
          },
        )
      },
    ),
  is_on_record: yup
    .boolean()
    .transform(val => val ?? false)
    .oneOf(
      [false],
      'You must go off the record. Do that by clicking the \u2018Go off record\u2019 button.',
    )
    .required(
      'You must go off the record. Do that by clicking the \u2018Go off record\u2019 button.',
    ),
  attendees_have_left_call: yup
    .boolean()
    .transform(val => val ?? false)
    .when(`$${EVENT_FORM_CONTEXT_IS_FINAL_SCRIPT_EVENT}`, {
      is: false,
      then: schema => schema,
      otherwise: schema =>
        schema
          .oneOf([true], 'Please confirm all attendees have left the call.')
          .required('Please confirm all attendees have left the call.'),
    }),
  spellings: yup
    .string()
    .max(
      DR_SPELLINGS_CHAR_LIMIT,
      getMaxCharactersMessage(DR_SPELLINGS_CHAR_LIMIT),
    )
    .nullable()
    .default(null),
  timestamps: yup
    .string()
    .max(
      DR_TIMESTAMPS_CHAR_LIMIT,
      getMaxCharactersMessage(DR_TIMESTAMPS_CHAR_LIMIT),
    )
    .nullable()
    .default(null),
})
